/**
 * @module LMCSContext
 * @description Contexto global para la gestión de la Ley Marco de Ciberseguridad (Ley 21.663).
 *
 * Administra el cumplimiento del marco regulatorio de ciberseguridad de Chile,
 * incluyendo artículos de la ley, incidentes cibernéticos y el estado OIV
 * (Operadores de Importancia Vital).
 *
 * Estructura de datos:
 * - `titles`: Los títulos/capítulos de la ley (cargados como datos globales, sin filtro por org).
 * - `articles`: Los artículos de cada título con su estado de cumplimiento y porcentaje.
 * - `incidents`: Incidentes cibernéticos de la organización (filtrados por `organization_id`).
 * - `oivStatus`: Estado de la organización como Operador de Importancia Vital.
 *
 * A diferencia de otros contextos, los títulos y artículos son datos globales (no varían
 * por organización) — el cumplimiento y estado de cada artículo sí es por organización.
 *
 * Tablas Supabase: `lmcs_titles`, `lmcs_articles`, `lmcs_cyber_incidents`, `lmcs_oiv_status`.
 *
 * @author Sistema ACSGC
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';
import {
  type LMCSTitle,
  type LMCSArticle,
  type CyberIncident,
  type OIVStatus,
  type LMCSMetrics
} from '../data/lmcsMockData';

/**
 * Estado OIV por defecto para organizaciones que aún no han completado
 * su registro como Operadores de Importancia Vital.
 */
const DEFAULT_OIV_STATUS: OIVStatus = {
  isDesignatedOIV: false,
  isRegistered: false,
  hasContinuityPlan: false,
  hasExternalAudit: false,
};

// =====================================================
// INTERFACES
// =====================================================

/**
 * Entrada del historial de cambios de estado de un artículo LMCS.
 * Se registra cada vez que `changeArticleStatus()` es llamada y se guarda
 * también en la tabla `lmcs_audit_trail` de Supabase.
 *
 * @property id - ID temporal generado localmente (no UUID de BD).
 * @property articleId - ID del artículo que fue modificado.
 * @property previousStatus / newStatus - Estado anterior y nuevo del artículo.
 * @property previousCompliance / newCompliance - Porcentaje antes y después del cambio.
 * @property note - Justificación del cambio ingresada por el usuario.
 * @property changedAt - Timestamp ISO del momento del cambio.
 * @property changedBy - Nombre del usuario que realizó el cambio.
 */
export interface ArticleHistoryEntry {
  id: string;
  articleId: string;
  previousStatus: LMCSArticle['status'];
  newStatus: LMCSArticle['status'];
  previousCompliance: number;
  newCompliance: number;
  note: string;
  changedAt: string;
  changedBy: string;
}

/**
 * Contrato del contexto LMCS.
 *
 * @property titles - Títulos/capítulos de la Ley Marco (datos globales, no por org).
 * @property articles - Artículos de la ley con estado de cumplimiento por organización.
 * @property incidents - Incidentes cibernéticos registrados por la organización.
 * @property oivStatus - Estado OIV de la organización (obligaciones de infraestructura crítica).
 * @property selectedArticle - Artículo actualmente seleccionado en la UI.
 * @property metrics - Métricas calculadas en tiempo real del estado de cumplimiento LMCS.
 * @property updateArticle - Actualiza campos de un artículo (estado, compliance, etc.).
 * @property assignReferents - Asigna referentes técnicos a un artículo.
 * @property createIncident - Crea un nuevo incidente cibernético y lo persiste en BD.
 * @property updateIncident - Actualiza campos de un incidente existente.
 * @property notifyIncident - Marca un incidente como notificado a la autoridad (CSIRT).
 * @property updateOIVStatus - Actualiza el estado OIV de la organización.
 * @property getArticlesByTitle - Filtra artículos por título de la ley.
 * @property getPendingIncidents - Retorna incidentes no notificados en estado 'detected'.
 * @property getOverdueIncidents - Retorna incidentes vencidos sin notificar.
 * @property articleHistory - Historial local de cambios de estado de artículos.
 * @property changeArticleStatus - Cambia estado+compliance de un artículo y registra historial.
 * @property getArticleHistory - Retorna el historial de cambios de un artículo específico.
 */
interface LMCSContextType {
  titles: LMCSTitle[];
  articles: LMCSArticle[];
  incidents: CyberIncident[];
  oivStatus: OIVStatus;
  isLoading: boolean;
  error: string | null;
  selectedArticle: LMCSArticle | null;
  setSelectedArticle: (article: LMCSArticle | null) => void;
  metrics: LMCSMetrics;
  updateArticle: (articleId: string, updates: Partial<LMCSArticle>) => Promise<void>;
  assignReferents: (articleId: string, referentIds: string[]) => Promise<void>;
  createIncident: (incident: Omit<CyberIncident, 'id' | 'incidentNumber'>) => Promise<CyberIncident>;
  updateIncident: (incidentId: string, updates: Partial<CyberIncident>) => Promise<void>;
  notifyIncident: (incidentId: string) => Promise<void>;
  updateOIVStatus: (updates: Partial<OIVStatus>) => Promise<void>;
  refreshData: () => Promise<void>;
  getArticlesByTitle: (titleId: string) => LMCSArticle[];
  getTitleById: (titleId: string) => LMCSTitle | undefined;
  getArticleById: (articleId: string) => LMCSArticle | undefined;
  getPendingIncidents: () => CyberIncident[];
  getOverdueIncidents: () => CyberIncident[];
  articleHistory: ArticleHistoryEntry[];
  changeArticleStatus: (articleId: string, status: LMCSArticle['status'], compliancePercentage: number, note: string) => Promise<void>;
  getArticleHistory: (articleId: string) => ArticleHistoryEntry[];
}

/** Contexto interno — accesible solo mediante `useLMCS()`. */
const LMCSContext = createContext<LMCSContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface LMCSProviderProps {
  children: React.ReactNode;
}

/**
 * Proveedor del contexto LMCS (Ley Marco de Ciberseguridad).
 *
 * Gestiona la carga de títulos y artículos de la ley (datos globales),
 * los incidentes y estado OIV de la organización (datos por org),
 * y el historial local de cambios de estado.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function LMCSProvider({ children }: LMCSProviderProps) {
  const { user } = useAuth();

  // ── Estado ──────────────────────────────────────────────────────────────────
  const [titles, setTitles] = useState<LMCSTitle[]>([]);
  const [articles, setArticles] = useState<LMCSArticle[]>([]);
  const [incidents, setIncidents] = useState<CyberIncident[]>([]);
  const [oivStatus, setOIVStatus] = useState<OIVStatus>(DEFAULT_OIV_STATUS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<LMCSArticle | null>(null);
  const [articleHistory, setArticleHistory] = useState<ArticleHistoryEntry[]>([]);

  // ─────────────────────────────────────────────────────
  // CARGAR DATOS
  // ─────────────────────────────────────────────────────

  /**
   * Carga todos los datos LMCS desde Supabase.
   *
   * Estrategia de carga por tipo de datos:
   * - Títulos y artículos: datos globales, no requieren `organization_id`.
   * - Incidentes y OIV: datos por organización, solo se cargan si hay `user.organization_id`.
   *
   * El cliente se castea como `any` porque las tablas `lmcs_*` no están en los tipos
   * generados automáticamente por Supabase (`database.types.ts`).
   *
   * @returns `true` si los datos se cargaron con éxito; `false` si ocurrió un error.
   */
  const loadFromDatabase = useCallback(async () => {
    try {
      logger.info('[LMCS] Cargando datos desde BD...');

      // Cast necesario: tablas lmcs_* no están en database.types.ts generado
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;

      // Cargar títulos (datos globales — no requieren org_id)
      const { data: titlesData, error: titlesError } = await db
        .from('lmcs_titles')
        .select('*')
        .order('sort_order');

      if (titlesError) throw titlesError;

      // Cargar artículos (datos globales — no requieren org_id)
      const { data: articlesData, error: articlesError } = await db
        .from('lmcs_articles')
        .select(`
          *,
          lmcs_article_assignments (
            referent_id,
            technical_referents (*)
          )
        `)
        .order('article_number');

      if (articlesError) throw articlesError;

      // Cargar incidentes y OIV solo si hay organización del usuario
      let incidentsData: unknown[] = [];
      if (user?.organization_id) {
        const { data: inc, error: incError } = await db
          .from('lmcs_cyber_incidents')
          .select('*')
          .eq('organization_id', user.organization_id)
          .order('detected_at', { ascending: false });

        if (!incError) incidentsData = inc || [];

        const { data: oivData, error: oivError } = await db
          .from('lmcs_oiv_status')
          .select('*')
          .eq('organization_id', user.organization_id)
          .single();

        if (!oivError && oivData) {
          setOIVStatus({
            isDesignatedOIV: oivData.is_designated_oiv || false,
            designationDate: oivData.designation_date ?? undefined,
            registrationDeadline: oivData.registration_deadline ?? undefined,
            isRegistered: oivData.is_registered || false,
            hasContinuityPlan: oivData.has_continuity_plan || false,
            hasExternalAudit: oivData.has_external_audit || false,
            lastAuditDate: oivData.last_audit_date ?? undefined,
            nextAuditDate: oivData.next_audit_date ?? undefined,
          });
        }
      }

      // Transformar títulos (incluso si viene vacío)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedTitles: LMCSTitle[] = (titlesData || []).map((t: any) => ({
        id: t.id,
        number: t.number,
        name: t.name,
        description: t.description || '',
        icon: t.icon || 'FileText',
        color: t.color || 'red',
        sortOrder: t.sort_order,
        articles: []
      }));

      // Transformar artículos de BD a tipos TypeScript
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedArticles: LMCSArticle[] = (articlesData || []).map((a: any) => ({
        id: a.id,
        articleNumber: a.article_number,
        titleId: a.title_id,
        parentId: a.parent_id ?? undefined,
        name: a.name,
        content: a.content || '',
        guidance: a.guidance ?? undefined,
        appliesTo: (a.applies_to || ['all']) as LMCSArticle['appliesTo'],
        isMandatory: a.is_mandatory ?? true,
        status: (a.status || 'not_started') as LMCSArticle['status'],
        compliancePercentage: a.compliance_percentage || 0,
        regulatoryDeadline: a.regulatory_deadline ?? undefined,
        isOverdue: a.is_overdue || false,
        infractionType: (a.infraction_type ?? undefined) as LMCSArticle['infractionType'],
        maxPenaltyUTM: a.max_penalty_utm ?? undefined,
        assignedReferents: [],
        evidence: [],
        tasks: [],
        children: []
      }));

      // Transformar incidentes de BD a tipos TypeScript
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedIncidents: CyberIncident[] = (incidentsData as any[]).map((i: any) => ({
        id: i.id,
        incidentNumber: i.incident_number || `INC-${new Date(i.detected_at).getFullYear()}-${String(i.id).slice(-3)}`,
        severity: i.severity as CyberIncident['severity'],
        category: i.category as CyberIncident['category'],
        title: i.title,
        description: i.description || '',
        affectedSystems: i.affected_systems || [],
        detectedAt: i.detected_at,
        containedAt: i.contained_at,
        resolvedAt: i.resolved_at,
        notificationDeadline: i.notification_deadline,
        isNotified: i.is_notified || false,
        notifiedAt: i.notified_at,
        isOverdue: i.is_overdue || false,
        status: i.status as CyberIncident['status'],
        reportedBy: i.reported_by || 'Sistema',
        sourceSecurityEventId: i.source_security_event_id
      }));

      setTitles(transformedTitles);
      setArticles(transformedArticles);
      setIncidents(transformedIncidents);

      logger.info(`[LMCS] ✅ Cargados ${transformedTitles.length} títulos, ${transformedArticles.length} artículos, ${transformedIncidents.length} incidentes`);
      return true;
    } catch (err) {
      logger.error('[LMCS] Error cargando desde BD:', err);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organization_id]);

  /**
   * Orquesta la carga de datos LMCS. Gestiona loading y error states.
   * A diferencia de CIS, LMCS no tiene datos mock de respaldo — si la BD falla
   * se muestra un mensaje de error al usuario.
   */
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loaded = await loadFromDatabase();
      if (!loaded) {
        setError('No se pudieron cargar los datos LMCS. Verifica la conexión con la base de datos.');
      }
    } catch (err) {
      logger.error('[LMCS] Error general:', err);
      setError('Error al cargar datos LMCS');
    } finally {
      setIsLoading(false);
    }
  }, [loadFromDatabase]);

  /** Carga inicial de datos al montar el proveedor. */
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // ─────────────────────────────────────────────────────
  // MÉTRICAS
  // ─────────────────────────────────────────────────────

  /**
   * Calcula métricas de cumplimiento LMCS en tiempo real a partir del estado actual.
   *
   * Siempre calcula desde el estado local (no desde los datos de BD directamente),
   * lo que permite que los incidentes creados dinámicamente aparezcan inmediatamente
   * en las métricas sin necesidad de recargar desde Supabase.
   *
   * Incluye: resumen por estado (compliant/in_progress/etc.), compliance por título,
   * artículos críticos pendientes, artículos vencidos, incidentes sin notificar y
   * riesgo total de multas en UTM.
   */
  const metrics = React.useMemo((): LMCSMetrics => {
    // Siempre calcular desde estado actual (tanto demo como producción)
    // para reflejar incidentes creados dinámicamente sin recargar de BD
    const allArticles = articles;

    const compliantArticles = allArticles.filter(a => a.status === 'compliant').length;
    const inProgressArticles = allArticles.filter(a => a.status === 'in_progress').length;
    const notStartedArticles = allArticles.filter(a => a.status === 'not_started').length;
    const nonCompliantArticles = allArticles.filter(a => a.status === 'non_compliant').length;

    const overallCompliance = allArticles.length > 0
      ? Math.round(allArticles.reduce((sum, a) => sum + a.compliancePercentage, 0) / allArticles.length)
      : 0;

    // Breakdown de cumplimiento por título de la ley
    const byTitle = titles.map(title => {
      const titleArts = allArticles.filter(a => a.titleId === title.id);
      const compliant = titleArts.filter(a => a.status === 'compliant').length;
      const compliance = titleArts.length > 0
        ? Math.round(titleArts.reduce((sum, a) => sum + a.compliancePercentage, 0) / titleArts.length)
        : 0;

      return {
        titleId: title.id,
        titleNumber: title.number,
        titleName: title.name,
        total: titleArts.length,
        compliant,
        compliance
      };
    });

    // Artículos de infracción gravísima aún no cumplidos (máximo riesgo legal)
    const criticalPending = allArticles.filter(
      a => a.infractionType === 'very_serious' && a.status !== 'compliant'
    ).length;

    const overdueArticles = allArticles.filter(a => a.isOverdue);

    // Incidentes detectados que aún no han sido notificados a la autoridad
    const pendingIncidents = incidents.filter(
      i => !i.isNotified && i.status === 'detected'
    );

    // Suma de multas máximas posibles por artículos no cumplidos (en UTM)
    const totalPenaltyRisk = allArticles
      .filter(a => a.status !== 'compliant' && a.maxPenaltyUTM)
      .reduce((sum, a) => sum + (a.maxPenaltyUTM || 0), 0);

    return {
      totalArticles: allArticles.length,
      compliantArticles,
      inProgressArticles,
      notStartedArticles,
      nonCompliantArticles,
      overallCompliance,
      byTitle,
      criticalPending,
      overdueArticles,
      pendingIncidents,
      oivStatus,
      totalPenaltyRisk
    };
  }, [articles, titles, incidents, oivStatus]);

  // ─────────────────────────────────────────────────────
  // ACCIONES CRUD
  // ─────────────────────────────────────────────────────

  /**
   * Actualiza campos de un artículo LMCS (estado, compliance, etc.).
   * Usa patrón optimista: actualiza estado local y luego sincroniza con BD.
   * También actualiza `selectedArticle` si corresponde al mismo ID.
   *
   * @param articleId - UUID del artículo a actualizar.
   * @param updates - Campos a modificar en el artículo.
   */
  const updateArticle = useCallback(async (
    articleId: string,
    updates: Partial<LMCSArticle>
  ) => {
    // Actualización optimista del estado local
    setArticles(prev => prev.map(a =>
      a.id === articleId ? { ...a, ...updates } : a
    ));

    // Mantener sincronizado el panel de detalle si está abierto
    if (selectedArticle?.id === articleId) {
      setSelectedArticle(prev => prev ? { ...prev, ...updates } : null);
    }

    if (user?.organization_id) {
      try {
        const dbUpdates: Record<string, unknown> = {};
        if (updates.status) dbUpdates.status = updates.status;
        if (updates.compliancePercentage !== undefined) {
          dbUpdates.compliance_percentage = updates.compliancePercentage;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('lmcs_articles')
          .update(dbUpdates)
          .eq('id', articleId);

        if (error) throw error;
      } catch (err) {
        logger.error('[LMCS] Error actualizando artículo:', err);
        await refreshData();
      }
    }
  }, [user?.organization_id, selectedArticle, refreshData]);

  /**
   * Asigna referentes técnicos a un artículo LMCS.
   * Elimina todas las asignaciones previas antes de insertar las nuevas.
   *
   * @param articleId - UUID del artículo.
   * @param referentIds - Lista de UUIDs de los referentes a asignar.
   */
  const assignReferents = useCallback(async (
    articleId: string,
    referentIds: string[]
  ) => {
    if (user?.organization_id) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;
      try {
        // Eliminar asignaciones previas para reemplazarlas completamente
        await db
          .from('lmcs_article_assignments')
          .delete()
          .eq('article_id', articleId);

        if (referentIds.length > 0) {
          const assignments = referentIds.map(refId => ({
            article_id: articleId,
            referent_id: refId,
            assigned_by: user.id
          }));

          await db.from('lmcs_article_assignments').insert(assignments);
        }

        await refreshData();
      } catch (err) {
        logger.error('[LMCS] Error asignando referentes:', err);
        await refreshData();
      }
    }
  }, [user, refreshData]);

  // ─────────────────────────────────────────────────────
  // ACCIONES INCIDENTES
  // ─────────────────────────────────────────────────────

  // Cast permanente del cliente para operaciones sobre tablas lmcs_* no tipadas
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;

  /**
   * Crea un nuevo incidente cibernético para la organización.
   * Genera un número de incidente correlativo (ej. INC-2025-001) y lo persiste en BD.
   *
   * Soporta vinculación con un SecurityEvent existente via `sourceSecurityEventId`,
   * para trazabilidad de incidentes que escalaron desde eventos de seguridad.
   *
   * @param incident - Datos del incidente (sin `id` ni `incidentNumber`).
   * @returns El incidente creado con ID y número asignados.
   */
  const createIncident = useCallback(async (
    incident: Omit<CyberIncident, 'id' | 'incidentNumber'>
  ) => {
    const newIncident: CyberIncident = {
      ...incident,
      id: `inc-${Date.now()}`,
      incidentNumber: `INC-${new Date().getFullYear()}-${String(incidents.length + 1).padStart(3, '0')}`
    };

    logger.info('[LMCS] 📝 Creando incidente:', newIncident.incidentNumber);
    setIncidents(prev => [newIncident, ...prev]);

    if (user?.organization_id) {
      try {
        const insertData: Record<string, unknown> = {
          organization_id: user.organization_id,
          severity: incident.severity,
          category: incident.category,
          title: incident.title,
          description: incident.description,
          affected_systems: incident.affectedSystems,
          detected_at: incident.detectedAt,
          notification_deadline: incident.notificationDeadline,
          is_notified: incident.isNotified || false,
          is_overdue: incident.isOverdue || false,
          status: incident.status,
          reported_by: incident.reportedBy
        };

        if (incident.sourceSecurityEventId) {
          insertData.source_security_event_id = incident.sourceSecurityEventId;
          logger.info('[LMCS] 🔗 Vinculando con Security Event:', incident.sourceSecurityEventId);
        }

        const { data, error } = await db
          .from('lmcs_cyber_incidents')
          .insert(insertData)
          .select()
          .single();

        if (error) throw error;
        logger.info('[LMCS] ✅ Incidente guardado en BD:', data?.id);
      } catch (err) {
        logger.error('[LMCS] ❌ Error creando incidente en BD:', err);
      }
    }

    return newIncident;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organization_id, incidents.length]);

  /**
   * Actualiza campos de un incidente existente (estado, fechas de contención/resolución).
   * No recarga todos los datos; actualiza solo el incidente afectado en el estado local.
   *
   * @param incidentId - UUID del incidente a actualizar.
   * @param updates - Campos a modificar en el incidente.
   */
  const updateIncident = useCallback(async (
    incidentId: string,
    updates: Partial<CyberIncident>
  ) => {
    setIncidents(prev => prev.map(i =>
      i.id === incidentId ? { ...i, ...updates } : i
    ));

    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.status) dbUpdates.status = updates.status;
      if (updates.containedAt) dbUpdates.contained_at = updates.containedAt;
      if (updates.resolvedAt) dbUpdates.resolved_at = updates.resolvedAt;

      await db.from('lmcs_cyber_incidents').update(dbUpdates).eq('id', incidentId);
    } catch (err) {
      logger.error('[LMCS] Error actualizando incidente:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Marca un incidente como notificado al CSIRT u organismo regulador.
   * Registra el timestamp de notificación y cambia el estado a 'notified'.
   *
   * Según la Ley 21.663, los incidentes graves deben notificarse dentro de plazos
   * específicos; esta función registra el cumplimiento de esa obligación.
   *
   * @param incidentId - UUID del incidente que fue notificado.
   */
  const notifyIncident = useCallback(async (incidentId: string) => {
    const now = new Date().toISOString();

    setIncidents(prev => prev.map(i =>
      i.id === incidentId
        ? { ...i, isNotified: true, notifiedAt: now, status: 'notified' as const }
        : i
    ));

    try {
      await db.from('lmcs_cyber_incidents').update({
        is_notified: true,
        notified_at: now,
        status: 'notified'
      }).eq('id', incidentId);
    } catch (err) {
      logger.error('[LMCS] Error notificando incidente:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────
  // ACCIONES OIV
  // ─────────────────────────────────────────────────────

  /**
   * Actualiza el estado OIV (Operador de Importancia Vital) de la organización.
   * Usa upsert para crear o actualizar el registro según si ya existe en BD.
   *
   * El estado OIV determina qué obligaciones adicionales aplican a la organización
   * según la Ley Marco de Ciberseguridad (planes de continuidad, auditorías externas, etc.).
   *
   * @param updates - Campos del estado OIV a actualizar.
   */
  const updateOIVStatus = useCallback(async (updates: Partial<OIVStatus>) => {
    setOIVStatus(prev => ({ ...prev, ...updates }));

    if (user?.organization_id) {
      try {
        await db.from('lmcs_oiv_status').upsert({
          organization_id: user.organization_id,
          is_designated_oiv: updates.isDesignatedOIV,
          is_registered: updates.isRegistered,
          has_continuity_plan: updates.hasContinuityPlan,
          has_external_audit: updates.hasExternalAudit,
          designation_date: updates.designationDate,
          registration_deadline: updates.registrationDeadline,
          last_audit_date: updates.lastAuditDate,
          next_audit_date: updates.nextAuditDate,
        });
      } catch (err) {
        logger.error('[LMCS] Error actualizando estado OIV:', err);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organization_id]);

  // ─────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────

  /** Retorna todos los artículos pertenecientes a un título específico de la ley. */
  const getArticlesByTitle = useCallback((titleId: string) => {
    return articles.filter(a => a.titleId === titleId);
  }, [articles]);

  /** Busca un título LMCS por su UUID. */
  const getTitleById = useCallback((titleId: string) => {
    return titles.find(t => t.id === titleId);
  }, [titles]);

  /** Busca un artículo LMCS por su UUID. */
  const getArticleById = useCallback((articleId: string) => {
    return articles.find(a => a.id === articleId);
  }, [articles]);

  /** Retorna incidentes detectados que aún no han sido notificados a la autoridad. */
  const getPendingIncidents = useCallback(() => {
    return incidents.filter(i => !i.isNotified && i.status === 'detected');
  }, [incidents]);

  /** Retorna incidentes vencidos (fuera de plazo de notificación) que no han sido notificados. */
  const getOverdueIncidents = useCallback(() => {
    return incidents.filter(i => i.isOverdue && !i.isNotified);
  }, [incidents]);

  /**
   * Cambia el estado y porcentaje de cumplimiento de un artículo, registrando
   * el cambio en el historial local y en la tabla `lmcs_audit_trail` de Supabase.
   *
   * Diferencia con `updateArticle`: esta función agrega trazabilidad completa
   * con el estado anterior/nuevo, la nota justificativa y el autor del cambio.
   *
   * @param articleId - UUID del artículo a modificar.
   * @param newStatus - Nuevo estado del artículo.
   * @param newCompliance - Nuevo porcentaje de cumplimiento (0-100).
   * @param note - Justificación del cambio (requerida para el historial).
   */
  const changeArticleStatus = useCallback(async (
    articleId: string,
    newStatus: LMCSArticle['status'],
    newCompliance: number,
    note: string
  ) => {
    const article = articles.find(a => a.id === articleId);
    if (!article) return;

    const entry: ArticleHistoryEntry = {
      id: `hist-${Date.now()}`,
      articleId,
      previousStatus: article.status,
      newStatus,
      previousCompliance: article.compliancePercentage,
      newCompliance,
      note,
      changedAt: new Date().toISOString(),
      changedBy: user?.name || 'Usuario'
    };

    await updateArticle(articleId, { status: newStatus, compliancePercentage: newCompliance });

    setArticleHistory(prev => [entry, ...prev]);

    if (user?.organization_id) {
      try {
        await db.from('lmcs_audit_trail').insert({
          organization_id: user.organization_id,
          entity_type: 'article',
          entity_id: articleId,
          action_type: 'status_change',
          action_description: note,
          user_id: user.id,
          user_name: user.name,
          previous_value: { status: entry.previousStatus, compliance_percentage: entry.previousCompliance },
          new_value: { status: newStatus, compliance_percentage: newCompliance }
        });
      } catch (err) {
        logger.error('[LMCS] Error escribiendo audit trail:', err);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles, user, updateArticle]);

  /**
   * Retorna el historial de cambios de estado de un artículo específico,
   * ordenado del más reciente al más antiguo.
   *
   * @param articleId - UUID del artículo del cual obtener el historial.
   */
  const getArticleHistory = useCallback((articleId: string) => {
    return articleHistory.filter(h => h.articleId === articleId);
  }, [articleHistory]);

  // ─────────────────────────────────────────────────────
  // VALOR DEL CONTEXTO
  // ─────────────────────────────────────────────────────

  const value: LMCSContextType = {
    titles,
    articles,
    incidents,
    oivStatus,
    isLoading,
    error,
    selectedArticle,
    setSelectedArticle,
    metrics,
    updateArticle,
    assignReferents,
    createIncident,
    updateIncident,
    notifyIncident,
    updateOIVStatus,
    refreshData,
    getArticlesByTitle,
    getTitleById,
    getArticleById,
    getPendingIncidents,
    getOverdueIncidents,
    articleHistory,
    changeArticleStatus,
    getArticleHistory
  };

  return (
    <LMCSContext.Provider value={value}>
      {children}
    </LMCSContext.Provider>
  );
}

// =====================================================
// HOOK
// =====================================================

/**
 * Hook para consumir el contexto LMCS desde cualquier componente
 * hijo de `LMCSProvider`.
 *
 * @throws Error si se usa fuera de `LMCSProvider`.
 * @returns Estado completo y operaciones del contexto LMCS.
 *
 * @example
 * const { articles, metrics, createIncident } = useLMCS();
 * const pendingIncidents = getPendingIncidents();
 * // Crear un nuevo incidente
 * await createIncident({ severity: 'high', category: 'data_breach', ... });
 */
export function useLMCS(): LMCSContextType {
  const context = useContext(LMCSContext);
  if (context === undefined) {
    throw new Error('useLMCS debe usarse dentro de un LMCSProvider');
  }
  return context;
}

export default LMCSContext;
